import { HttpErrorResponse, HttpEvent, HttpHandler, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import * as Sentry from '@sentry/browser';
import { RouterService } from '../../utilities/router/router.service';
import { ToastService } from '../../services/toast/toast.service';
import { TranslateService } from '../../utilities/translate/translate.service';

@Injectable({
  providedIn: 'root'
})
export class ErrorInterceptorService {

  private readonly ERROR_DURATION = 999999;

  constructor(
    private routerService: RouterService,
    private toastService: ToastService,
    private translateService: TranslateService
  ) { }

  /**
   * Interceptor.
   *
   * Catch errors in api calls and show error message.
   *
   * @note There can only be *one* error handling interceptor active at the same time.
   *
   * @param req HTTP request.
   * @param next HTTP handler.
   * @returns A HttpEvent observable with the current http stream.
   */
  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        Sentry.captureException(error);

        switch (error?.status) {
          case 401:
            this._showUnauthorized();
            break;
          case 403:
            this._showForbidden();
            break;
          default:
            this._showGeneralError();
            break;
        }

        return throwError(error);
      })
    );
  }

  /**
   * Show general error message.
   *
   * Show message and trigger page reload.
   */
  private _showGeneralError(): void {
    this.toastService.open(
      this.translateService.get('errorMessageGeneral'),
      this.translateService.get('restart'),
      { duration: this.ERROR_DURATION, panelClass: 'error' }
    ).afterDismissed().subscribe(() => {
      location.reload();
    });
  }

  /**
   * 403: Forbidden.
   *
   * Show message and warn user.
   */
  private _showForbidden(): void {
    this.toastService.open(
      this.translateService.get('errorMessageForbidden'),
      this.translateService.get('ok'),
      { duration: this.ERROR_DURATION, panelClass: 'error' }
    ).afterDismissed().subscribe(() => {
      location.reload();
    });
  }

  /**
   * 401: Unauthorized.
   *
   * Show message and forward to login screen.
   */
  private _showUnauthorized(): void {
    this.toastService.open(
      this.translateService.get('errorMessageUnauthorized'),
      this.translateService.get('toLoginScreen'),
      { duration: this.ERROR_DURATION, panelClass: 'error' }
    ).afterDismissed().subscribe(() => {
      this.routerService.goToLogin();
    });
  }
}
